import {
  useMemo,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { PanelContext } from "../PageLayout/PanelContext";
import { PageEdgeContext } from "../PageLayout/Internal/PageEdgeContext";
import { TableProvider } from "./Internal/TableContext";
import { ColumnSpacingContext } from "./Internal/ColumnSpacingContext";
import { ColumnEditorPopover_ } from "./Internal/ColumnEditor";
import type { Column } from "./Internal/Column";
import {
  ACTIONS_COLUMN_KEY,
  buildVisibleColumns,
} from "./Internal/tableColumns";
import { useTableConfigParam } from "../Hooks/useTableConfigParam";
import { useColumnConfigurations } from "./Internal/useColumnConfigurations";
import { useTableSelection } from "./Internal/useTableSelection";
import { useTablePagination } from "./Internal/useTablePagination";
import {
  SelectionCheckbox_,
  TableHeaderCell_,
  TableRow_,
  SelectionBanner_,
  type RowHighlightColor,
} from "./Internal/TableParts";
import { PaginationBar_ } from "./Internal/TablePagination";
import type {
  AllMatchingSelection,
  TableServerMode,
  TableServerQuery,
} from "./Internal/TableServerMode";

export type { RowHighlightColor };
export type { AllMatchingSelection, TableServerMode, TableServerQuery };
export type { Column } from "./Internal/Column";

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowKey?: (row: T) => string;
  /**
   * How much horizontal room sits between columns.
   *
   * Only worth changing for a table whose columns all hug their content: with nothing absorbing
   * surplus width between them, the gaps are exactly this padding, and the default is tight enough
   * that adjacent columns read as one.
   *
   * Reaches body cells and data header cells only. The selection, expand and right-column header
   * cells keep their fixed padding, so a `selectable` or `configurable` table set to `wide` has its
   * header checkbox / column-editor gear out of line with the rows beneath.
   */
  columnSpacing?: "default" | "wide";
  emptyMessage?: string;
  configurable?: boolean;
  tableId?: string;
  selectedRowPredicate?: (row: T) => boolean;
  /** Return a color key to highlight specific rows. Takes precedence over selectedRowPredicate. */
  rowHighlight?: ((row: T) => RowHighlightColor | undefined) | undefined;
  selectable?: boolean;
  /**
   * Why a given row can't be selected, or undefined when it can. Returning a
   * reason greys its checkbox and explains itself on hover — a disabled control
   * with no explanation reads as broken rather than ineligible, same rule as
   * Button's disabledMessage. Rows with a reason are skipped by select-all.
   */
  selectionDisabledReason?: (row: T) => ReactNode | undefined;
  onSelectionChange?: (selectedKeys: Set<string>) => void;
  /** Seeds the initial selected row keys on mount. Useful when the parent wants to start with rows pre-selected (e.g. "all eligible by default"). Only read on first mount; later changes are ignored. */
  initialSelectedKeys?: Set<string> | undefined;
  pageSize?: number;
  pageSizeOptions?: number[];
  /**
   * Return to page 1 whenever this changes. Pass whatever decides which rows
   * you're handing in — a search term, a filter selection. The row count on its
   * own misses the case where the filter changes but the count doesn't.
   * Derive it from values, never from something fresh on every render, or
   * the table can never leave page 1.
   */
  resetPageKey?: string | number | undefined;
  footerAction?: ReactNode;
  /** Overrides the default "X total rows" text in the pagination bar. */
  footerLeft?: ReactNode | undefined;
  /** Content shown on the right side of the pagination bar (e.g. totals). */
  footerRight?: ReactNode | undefined;
  /**
   * The rows are one page of a larger set that the server sorts and pages.
   * Columns sorted this way set `serverSortable`. Requires `pageSize`.
   */
  server?: TableServerMode | undefined;
  /**
   * Hoists matching rows above the rest, each group keeping the current sort
   * order. For surfacing a subset — the shipments behind a stat the reader just
   * clicked — without hiding the remainder the way a filter would. Pair with
   * `rowHighlight` so the hoisted rows stay recognisable, and with
   * `resetPageKey` so a change returns the reader to page 1.
   */
  pinnedRowPredicate?: ((row: T) => boolean) | undefined;
  /** Column key to sort by on initial render (before any user interaction). */
  defaultSortKey?: string | undefined;
  /** Sort direction for the default sort key. Defaults to "asc". */
  defaultSortDir?: "asc" | "desc" | undefined;
  /** Inline action buttons in rightmost column (wrapped in ActionCell.actions). */
  rowActions?: ((row: T) => ReactNode) | undefined;
  /** Overflow menu items wrapped in a kebab Menu. Coexists with rowActions. */
  rowMenu?: ((row: T) => ReactNode) | undefined;
  /** When set, each row gets a trailing chevron that toggles an inline detail
   *  panel rendering this content in a full-width row beneath it. */
  expandedRowRender?: ((row: T) => ReactNode) | undefined;
  /** Buttons rendered on the right of the selection banner when rows are selected. clearSelection resets the selection (e.g. after a successful bulk action). */
  bulkActions?:
    | ((state: {
        selectedKeys: Set<string>;
        clearSelection: () => void;
      }) => ReactNode)
    | undefined;
  /** Pin the selection banner (count, Clear selection, bulkActions) to the top of
   *  the window while scrolling, so the bulk actions stay in reach after
   *  selecting rows far down a long table. */
  stickyBulkActions?: boolean | undefined;
}

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function Table<T>({
  columns,
  data,
  rowKey,
  columnSpacing = "default",
  emptyMessage = "No data",
  configurable,
  tableId: rawTableId,
  selectedRowPredicate,
  selectionDisabledReason,
  rowHighlight,
  selectable,
  onSelectionChange,
  initialSelectedKeys,
  pageSize: initialPageSize,
  pageSizeOptions = [10, 25, 50, 100],
  resetPageKey,
  server,
  pinnedRowPredicate,
  footerAction,
  footerLeft,
  footerRight,
  defaultSortKey,
  defaultSortDir,
  rowActions,
  rowMenu,
  bulkActions,
  stickyBulkActions = false,
  expandedRowRender,
}: TableProps<T>) {
  const insidePanel = useContext(PanelContext);
  const atPageEdge = useContext(PageEdgeContext);

  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const expandable = expandedRowRender !== undefined;
  const handleToggleExpand = useCallback(
    (key: string) =>
      setExpandedKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    [],
  );

  // Derive a stable tableId from column keys when not provided.
  // Without this, all anonymous tables share localStorage and URL params.
  const tableId = rawTableId ?? columns.map((c) => c.key).join(",");

  // ── Column config (visibility + order + sort) — synced to URL ──
  const {
    hiddenKeys,
    columnOrder,
    sortKey,
    sortDir,
    setHiddenKeys,
    setColumnOrder,
    setSort,
    saveAsDefault,
  } = useTableConfigParam(
    tableId,
    columns.map((c) => ({ key: c.key, defaultHidden: c.defaultHidden })),
    defaultSortKey,
    defaultSortDir,
    configurable,
  );

  // ── Column configurations (localStorage, columns + sort only — no filters) ──
  const {
    configurations,
    activeConfigId,
    isDirty,
    handleSaveConfiguration,
    handleUpdateConfiguration,
    handleLoadConfiguration,
    handleDeleteConfiguration,
    handleDuplicateConfiguration,
  } = useColumnConfigurations(
    tableId,
    hiddenKeys,
    columnOrder,
    sortKey,
    sortDir,
    (keys) => setHiddenKeys(keys),
    setColumnOrder,
    setSort,
  );

  const sortedData = useMemo(() => {
    // Server rows arrive in order; a comparator here would only scramble
    // one page of them.
    if (server || !sortKey) return data;
    const col = columns.find((c) => c.key === sortKey);
    if (!col?.sortable) return data;
    const isBlank = col.sortBlank;
    if (!isBlank) {
      const sorted = [...data].sort(col.sortable);
      return sortDir === "desc" ? sorted.reverse() : sorted;
    }
    const filled: T[] = [];
    const blank: T[] = [];
    for (const row of data) {
      if (isBlank(row)) blank.push(row);
      else filled.push(row);
    }
    filled.sort(col.sortable);
    if (sortDir === "desc") filled.reverse();
    return [...filled, ...blank];
  }, [data, columns, sortKey, sortDir, server]);

  const orderedData = useMemo(() => {
    if (!pinnedRowPredicate) return sortedData;
    const pinned: T[] = [];
    const rest: T[] = [];
    for (const row of sortedData) {
      if (pinnedRowPredicate(row)) pinned.push(row);
      else rest.push(row);
    }
    return [...pinned, ...rest];
  }, [sortedData, pinnedRowPredicate]);

  const {
    paginated,
    currentPageSize,
    currentPage,
    totalPages,
    displayData,
    setCurrentPageSize,
    setCurrentPage,
  } = useTablePagination({
    initialPageSize,
    sortedData: orderedData,
    resetPageKey,
    serverTotalRows: server?.totalRows,
  });

  // The callback is read through a ref so a caller that passes a fresh
  // `server` object every render (they all do) doesn't refetch every render.
  const onServerQueryChange = useRef(server?.onQueryChange);
  onServerQueryChange.current = server?.onQueryChange;
  // `resetPageKey` is a dependency of its own: a filter change that lands on
  // page 1 leaves every other value as it was, and the caller still needs a
  // fresh page for the new filter.
  const serverPaged = server !== undefined;
  useEffect(() => {
    if (!serverPaged) return;
    onServerQueryChange.current?.({
      page: currentPage,
      pageSize: currentPageSize,
      sortKey,
      sortDir,
    });
  }, [
    serverPaged,
    currentPage,
    currentPageSize,
    sortKey,
    sortDir,
    resetPageKey,
  ]);

  const getRowKey = useCallback(
    (row: T, index: number) => (rowKey ? rowKey(row) : String(index)),
    [rowKey],
  );

  const {
    selectedKeys,
    selectedCount,
    isRowSelected,
    allSelected,
    someSelected,
    allDataSelected,
    hasMoreData,
    showSelectionBanner,
    handleSelectAll,
    handleSelectAllData,
    handleClearAllSelection,
    handleSelectRow,
  } = useTableSelection({
    selectable: selectable === true,
    paginated,
    displayData,
    sortedData,
    getRowKey,
    onSelectionChange,
    initialSelectedKeys,
    isRowDisabled: selectionDisabledReason
      ? (row) => selectionDisabledReason(row) !== undefined
      : undefined,
    server,
  });

  function handleSort(key: string) {
    setSort(key, sortKey === key && sortDir === "asc" ? "desc" : "asc");
    setCurrentPage(0);
  }

  const visibleColumns = useMemo(
    () => buildVisibleColumns(columns, columnOrder, hiddenKeys),
    [columns, columnOrder, hiddenKeys],
  );

  const hasRowActions = rowActions !== undefined || rowMenu !== undefined;
  const actionsHidden = hiddenKeys.has(ACTIONS_COLUMN_KEY);
  const showActionsCell = hasRowActions && !actionsHidden;
  const showRightCol = configurable || hasRowActions;

  const handleToggle = (key: string) =>
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const totalColSpan =
    visibleColumns.length +
    (showRightCol ? 1 : 0) +
    (selectable ? 1 : 0) +
    (expandable ? 1 : 0);

  const selectionBanner = (
    <SelectionBanner_
      visible={showSelectionBanner}
      selectedCount={selectedCount}
      totalRowCount={server?.totalRows ?? sortedData.length}
      allDataSelected={allDataSelected}
      hasMoreData={hasMoreData}
      onSelectAllData={handleSelectAllData}
      onClearSelection={handleClearAllSelection}
      sticky={stickyBulkActions}
      actions={bulkActions?.({
        selectedKeys,
        clearSelection: handleClearAllSelection,
      })}
    />
  );

  return (
    <ColumnSpacingContext.Provider value={columnSpacing}>
      <TableProvider value={true}>
        <div>
          {/* Outside the scroll wrapper when sticky: an overflow container
              would capture the sticky positioning and the banner would scroll
              away with the page. */}
          {selectable && stickyBulkActions && selectionBanner}
          <div
            className={
              insidePanel
                ? // Panels are square (rounded-none); keep the table's scroll
                  // container square too, or row hover/selection backgrounds get
                  // clipped into rounded bottom corners on the last row.
                  "overflow-x-auto bg-card"
                : // Ledger style: crisp 1px horizontal rules only — no side
                  // borders, no rounded corners. Top/bottom caps, the header
                  // rule, and the body dividers all share the single `muted-foreground`
                  // (dark) tone so every hairline matches. The caps/header stay
                  // inside the page gutter (outer px-6 below) so they don't bleed
                  // to the viewport edge.
                  "overflow-auto border-y border-muted-foreground bg-card"
            }
          >
            {selectable && !stickyBulkActions && selectionBanner}
            <table
              className="w-full text-sm"
              data-testid={tableId ? `table-${tableId}` : undefined}
            >
              <thead>
                <tr
                  className={
                    // Header rule is a crisp 1px in the `muted-foreground` (dark) tone,
                    // matching the caps, body dividers, and footer.
                    "border-b border-muted-foreground bg-card"
                  }
                  data-testid="table-header"
                >
                  {selectable && (
                    <th
                      className={`w-10 ${insidePanel ? "px-4 first:pl-0" : "px-1.5 2xl:px-4"} py-2 text-center 2xl:py-3 ${atPageEdge ? "first:pl-6" : ""}`}
                    >
                      <SelectionCheckbox_
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={handleSelectAll}
                        testId="table-select-all"
                      />
                    </th>
                  )}
                  {visibleColumns.map((col) => (
                    <TableHeaderCell_
                      key={col.key}
                      col={col as Column<unknown>}
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                  ))}
                  {showRightCol && (
                    <th
                      className={`${hasRowActions ? "" : "w-10"} ${insidePanel ? "px-4 last:pr-0" : "px-1.5 2xl:px-2"} py-2 text-right 2xl:py-3 ${atPageEdge ? "last:pr-6" : ""}`}
                    >
                      {configurable && (
                        <ColumnEditorPopover_
                          columns={columns.map((c) => ({
                            key: c.key,
                            header: c.header,
                          }))}
                          hiddenKeys={hiddenKeys}
                          columnOrder={columnOrder}
                          onToggle={handleToggle}
                          onReorder={setColumnOrder}
                          onSaveDefault={tableId ? saveAsDefault : undefined}
                          {...(hasRowActions && {
                            actionsHidden,
                            onToggleActions: () =>
                              handleToggle(ACTIONS_COLUMN_KEY),
                          })}
                          configurations={configurations}
                          activeConfigId={activeConfigId}
                          onSaveConfiguration={
                            tableId ? handleSaveConfiguration : undefined
                          }
                          onUpdateConfiguration={
                            tableId ? handleUpdateConfiguration : undefined
                          }
                          onLoadConfiguration={
                            tableId ? handleLoadConfiguration : undefined
                          }
                          onDeleteConfiguration={
                            tableId ? handleDeleteConfiguration : undefined
                          }
                          onDuplicateConfiguration={
                            tableId ? handleDuplicateConfiguration : undefined
                          }
                          isDirty={isDirty}
                        />
                      )}
                    </th>
                  )}
                  {expandable && (
                    <th
                      className={`w-10 ${insidePanel ? "px-4 last:pr-0" : "px-1.5 2xl:px-2"} py-2 2xl:py-3 ${atPageEdge ? "last:pr-6" : ""}`}
                      aria-hidden="true"
                    />
                  )}
                </tr>
              </thead>
              <tbody>
                {displayData.map((row, rowIndex) => {
                  const key = getRowKey(row, rowIndex);
                  const highlightColor =
                    rowHighlight?.(row) ??
                    (selectedRowPredicate?.(row) ? "green" : undefined);
                  const isChecked = selectable === true && isRowSelected(key);
                  const disabledReason = selectionDisabledReason?.(row);
                  return (
                    <TableRow_
                      key={key}
                      rowKey={key}
                      row={row}
                      columns={visibleColumns as Column<unknown>[]}
                      highlightColor={highlightColor}
                      isChecked={isChecked}
                      selectionDisabledReason={disabledReason}
                      selectable={selectable === true}
                      showRightCol={showRightCol}
                      rowActions={
                        showActionsCell && rowActions
                          ? (rowActions as (row: unknown) => ReactNode)
                          : undefined
                      }
                      rowMenu={
                        showActionsCell && rowMenu
                          ? (rowMenu as (row: unknown) => ReactNode)
                          : undefined
                      }
                      onSelectRow={handleSelectRow}
                      isExpanded={expandedKeys.has(key)}
                      onToggleExpand={handleToggleExpand}
                      expandedRowRender={
                        expandedRowRender as
                          | ((row: unknown) => ReactNode)
                          | undefined
                      }
                    />
                  );
                })}
                {displayData.length === 0 && (
                  <tr>
                    <td
                      colSpan={totalColSpan}
                      className={`${insidePanel ? "px-4" : "px-2.5 2xl:px-4"} py-8 text-center text-muted-foreground 2xl:py-12`}
                    >
                      {emptyMessage}
                    </td>
                  </tr>
                )}
              </tbody>
              {footerAction && (
                <tfoot>
                  <tr className="border-t border-border">
                    <td
                      colSpan={totalColSpan}
                      className={`${insidePanel ? "px-4" : "px-2.5 2xl:px-4"} py-2 2xl:py-2.5`}
                    >
                      {footerAction}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          <PaginationBar_
            visible={paginated || !!footerLeft || !!footerRight}
            insidePanel={insidePanel}
            totalRows={server?.totalRows ?? data.length}
            selectedCount={selectable ? selectedCount : 0}
            currentPage={currentPage}
            totalPages={totalPages}
            currentPageSize={currentPageSize}
            pageSizeOptions={pageSizeOptions}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setCurrentPageSize(size);
              setCurrentPage(0);
            }}
            footerLeft={footerLeft}
            footerRight={footerRight}
            {...(rawTableId !== undefined && { testIdPrefix: rawTableId })}
          />
        </div>
      </TableProvider>
    </ColumnSpacingContext.Provider>
  );
}
